import React, { useState, useEffect } from "react";
import {
  Store,
  Package,
  ShieldCheck,
  Truck,
  ClipboardList,
  Boxes,
  TrendingUp,
  Wallet,
  CreditCard,
  Building2,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const Dccws = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [wholesaleData, setWholesaleData] = useState(null);
  const [primaryData, setPrimaryData] = useState(null);
  const [showCwsList, setShowCwsList] = useState(false);
  const location = useLocation();

  const DMS_BASE_URL = "https://santhai.kooturavu.tn.gov.in/api/v1";
  const AUTH_TOKEN = "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9";
  const [metrics, setMetrics] = useState({
    clientDepartments: 0,
    totalOrders: 0,
    totalSalesValue: 0,
    medianDeliveryTime: 0,
    loading: true,
    error: null
  });

  const cwsList = (wholesaleData?.cws_list || []).map(c => c.name || c);

  useEffect(() => {
    fetchData();
    fetchMetrics();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const wholesaleResponse = await fetch(`${API_BASE_URL}/dcc_wholesale_stores/get_dcc_wholesale_stores_content`);
      const wholesaleResult = await wholesaleResponse.json();
      if (wholesaleResult.status === 'success' && wholesaleResult.data) {
        setWholesaleData(wholesaleResult.data);
      }

      const primaryResponse = await fetch(`${API_BASE_URL}/dcc_primary_stores/get_dcc_primary_stores_content`);
      const primaryResult = await primaryResponse.json();
      if (primaryResult.status === 'success' && primaryResult.data) {
        setPrimaryData(primaryResult.data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = '2024-09-19';

      const response = await fetch(
        `${DMS_BASE_URL}/indent_data?start=${startDate}&end=${endDate}`,
        {
          method: 'GET',
          headers: {
            'Authorization': AUTH_TOKEN,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch data');

      const result = await response.json();

      if (!result.error && result.data) {
        const indentData = result.data;
        const uniqueDepartments = new Set(indentData.map(item => item.department)).size;
        const totalOrders = indentData.length;
        const totalSales = indentData.reduce((sum, item) => sum + (item.dispatch_value || 0), 0);

        const deliveryTimes = indentData
          .map(item => item.delivered_days)
          .filter(days => days !== null && days !== undefined)
          .sort((a, b) => a - b);

        const median = deliveryTimes.length > 0
          ? deliveryTimes.length % 2 === 0
            ? (deliveryTimes[deliveryTimes.length / 2 - 1] + deliveryTimes[deliveryTimes.length / 2]) / 2
            : deliveryTimes[Math.floor(deliveryTimes.length / 2)]
          : 0;

        setMetrics({
          clientDepartments: uniqueDepartments,
          totalOrders: totalOrders,
          totalSalesValue: totalSales,
          medianDeliveryTime: median,
          loading: false,
          error: null
        });
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setMetrics(prev => ({ ...prev, loading: false, error: 'Failed to load metrics' }));
    }
  };

  const content = {
    en: {
      dccwsTitle: "District Central Cooperative Wholesale Stores",
      primaryTitle: "Primary Stores",
      overview: "Overview",
      keyFunctionality: "Key Functionality",
      keyStats: "Key Stats",
      cwsListTitle: "List of Cooperative Wholesale Stores",
      viewCwsList: "Click to view all CWS",
      keyActivities: "Key Activities",
      close: "Close",
      days: "days",
    },
    ta: {
      dccwsTitle: "மாவட்ட மொத்த விற்பனைக் கூட்டுறவுப் பண்டகசாலைகள்",
      primaryTitle: "தொடக்கக் கூட்டுறவுப் பண்டகசாலைகள்",
      overview: "அறிமுகம்",
      keyFunctionality: "முக்கிய செயல்பாடுகள்",
      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
      cwsListTitle: "கூட்டுறவு மொத்த விற்பனைக் கடைகளின் பட்டியல்",
      viewCwsList: "அனைத்து CWSகளைப் பார்க்க கிளிக் செய்யவும்",
      keyActivities: "முக்கிய செயல்பாடுகள்",
      close: "மூடு",
      days: "நாட்கள்",
    }
  };

  const t = content[language];

  // Static Tamil overrides (English continues from API)
  const staticTamil = {
    wholesaleOverview: "தமிழ்நாட்டின் கூட்டுறவு விநியோகத்தின் முதுகெலும்பாகத் திகழ்பவை மாவட்ட மொத்த விற்பனைக் கூட்டுறவுப் பண்டகசாலைகளாகும். மாவட்டங்களில் உள்ள நியாயவிலைக் கடைகள், தொடக்கக் கூட்டுறவுப் பண்டகசாலைகள் மற்றும் பிற கூட்டுறவு நிறுவனங்களுக்கு அத்தியாவசியப் பொருட்கள் தடையின்றி வழங்கப்படுவதை இவை உறுதி செய்கின்றன. கொள்முதல், சேமிப்பு, மொத்த விநியோகம் ஆகியவற்றை திறம்படக் கையாள்வதன்மூலம் அத்தியாவசியப் பொருட்களின் விலையினை நிலையாக வைத்திருப்பது, தேவையான நேரத்தில் கிடைக்கச் செய்வது, குடிமகன்களுக்கு வெளிப்படைத்தன்மையுடன் பொருட்களை விநியோகம் செய்வது உள்ளிட்ட பணிகளில் மொத்த விற்பனைப் பண்டகசாலைகள் முக்கிய பங்காற்றுகின்றன.",
    wholesaleKeyFunctionality: [
      "அத்தியாவசியப் பொருட்களை மொத்தமாகக் கொள்முதல் செய்தல்",
      "நியாயவிலைக் கடைகள் மற்றும் தொடக்கக் கூட்டுறவு நிறுவனங்களுக்கு விநியோகம்",
      "தரக் கட்டுப்பாடு மற்றும் இருப்புக் கண்காணிப்பு",
      "கிடங்கு வசதி மற்றும் பாதுகாப்பான சேமிப்பு",
      "தமிழ்நாடு நுகர்பொருள் வாணிபக் கழகம், குடிமைப் பொருள் வழங்கல் மற்றும் பொது விநியோகத் திட்டம் ஆகியவற்றுடன் ஒருங்கிணைப்பு",
      "உரிய நேரத்தில் இருப்பைச் சரிசெய்தல்"
    ],
    primaryOverview: "தொடக்கக் கூட்டுறவுப் பண்டகசாலைகள் அத்தியாவசியப் பொருட்களையும், அன்றாட வீட்டு உபயோகப் பொருட்களையும் நம்பகமான மற்றும் நியாயமான சில்லறை விற்பனை முறையின்மூலம் நேரடியாக மக்களிடம் கொண்டு சேர்க்கின்றன. அடித்தட்டு நிலையில் செயல்படும் இச்சங்கங்கள், ஊரக மற்றும் புறநகர்ப் பகுதிகளில் வாழும் குடும்பங்களுக்குத் தரமான பொருட்கள், நியாயமான விலை, நம்பகமான சேவை ஆகியவை வழங்கப்படுதலை உறுதி செய்கின்றன. கூட்டுறவுச் சங்கிலியின் இறுதி இணைப்பாகச் செயல்படும் இவை, குடும்பச் செலவுகளைக் குறைப்பதிலும் சமூக நலனை உறுதிப்படுத்துவதிலும் முக்கியப் பங்காற்றுகின்றன.",
    primaryKeyFunctionality: [
      "நியாயமான மற்றும் வெளிப்படையான விலை",
      "தர உறுதி செய்யப்பட்ட பொருட்கள்",
      "சமூகம் சார்ந்த சேவைகள்",
      "தொடர்ச்சியான இருப்புச் சரிபார்ப்பு",
      "உள்ளூர் உற்பத்தியாளர்களுக்கு ஆதரவு"
    ],
  };

  useEffect(() => {
    const handleHashScroll = () => {
      if (location.hash) {
        const id = location.hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          const offset = 180;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    const timer = setTimeout(handleHashScroll, 100);
    return () => clearTimeout(timer);
  }, [location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-blue-50 border-b border-blue-100">
        <div className="mx-auto px-4 py-10">
          <h1 className="text-3xl font-bold text-gray-800 text-center">
            {t.dccwsTitle}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto px-4 py-8">

        {/* ===== DCCWS SECTION ===== */}
        {wholesaleData && (
          <section id="dccws-section" className="bg-white p-8 rounded-lg shadow-sm mb-4">

            {/* Overview */}
            <div id="dccws-overview" className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.overview}</h3>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed">
                  {language === 'en' ? wholesaleData.overview_eng : staticTamil.wholesaleOverview}
                </p>
              </div>
            </div>

            {/* Key Functionality */}
            {(() => {
              const items = language === 'ta'
                ? staticTamil.wholesaleKeyFunctionality.map((text) => ({ content_tamil: text }))
                : (wholesaleData.key_functionality || []);
              if (items.length === 0) return null;
              return (
                <div id="dccws-functionality" className="mb-10">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyFunctionality}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((item, i) => (
                      <div key={i} className="bg-blue-50 p-4 rounded-lg flex items-start gap-3">
                        <div className="text-blue-600 mt-1">
                          {[<Package size={20} />, <Truck size={20} />, <ShieldCheck size={20} />, <Boxes size={20} />, <ClipboardList size={20} />, <Store size={20} />][i % 6]}
                        </div>
                        <p className="text-gray-700 text-sm">
                          {language === 'en' ? item.content_eng : item.content_tamil}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* CWS List - Collapsible */}
            <div id="cws-list-section" className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">{t.cwsListTitle}</h3>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <button
                  onClick={() => setShowCwsList(!showCwsList)}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-md"
                >
                  <span className="text-lg font-semibold">{t.viewCwsList}</span>
                  <svg
                    className={`w-6 h-6 transition-transform duration-300 ${showCwsList ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showCwsList && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                    {cwsList.map((cws, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100"
                      >
                        <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-xs">{index + 1}</span>
                        </div>
                        <span className="text-gray-800 text-sm font-medium">{cws}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Key Activities */}
            {(wholesaleData.key_activities || []).length > 0 && (
              <div id="dccws-key-activities">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyActivities}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {wholesaleData.key_activities.map((activity, i) => (
                    <div key={i} className="bg-amber-50 border border-amber-100 p-4 rounded-lg flex items-center gap-3">
                      <div className="w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0">
                        {i + 1}
                      </div>
                      <p className="text-gray-700 text-sm font-medium">
                        {language === 'en' ? (activity.value_eng || '') : (activity.value_tamil || activity.value_eng || '')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </section>
        )}

        {/* ===== PRIMARY STORES HEADER ===== */}
        <div className="bg-blue-50 border-b border-blue-100 mt-4">
          <div className="mx-auto px-4 py-10">
            <h1 className="text-3xl font-bold text-gray-800 text-center">
              {t.primaryTitle}
            </h1>
          </div>
        </div>

        {/* ===== PRIMARY STORES SECTION ===== */}
        {primaryData && (
          <section id="primary-section" className="bg-white p-8 rounded-lg shadow-sm mt-4">

            {/* Overview */}
            <div id="primary-overview" className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.overview}</h3>
              <div className="bg-emerald-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed">
                  {language === 'en' ? primaryData.overview_eng : staticTamil.primaryOverview}
                </p>
              </div>
            </div>

            {/* Key Functions */}
            {(() => {
              const items = language === 'ta'
                ? staticTamil.primaryKeyFunctionality.map((text) => ({ content_tamil: text }))
                : (primaryData.key_functionality || []);
              if (items.length === 0) return null;
              return (
                <div id="primary-functionality">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyFunctionality}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((item, i) => (
                      <div key={i} className="bg-blue-50 p-4 rounded-lg flex items-start gap-3">
                        <div className="text-blue-600 mt-1">
                          <Store size={20} />
                        </div>
                        <p className="text-gray-700 text-sm">
                          {language === 'en' ? item.content_eng : item.content_tamil}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

          </section>
        )}

        {/* ===== KEY STATS - at the very end ===== */}
        {wholesaleData && wholesaleData.key_stats && wholesaleData.key_stats.length > 0 && (
          <section className="bg-white p-8 rounded-lg shadow-sm mt-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyStats}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {wholesaleData.key_stats.map((stat, i) => {
                const icons = [
                  <TrendingUp className="w-5 h-5" />,
                  <Wallet className="w-5 h-5" />,
                  <CreditCard className="w-5 h-5" />,
                  <Building2 className="w-5 h-5" />
                ];
                return (
                  <div key={i} className="bg-teal-700 rounded-lg p-4 text-white shadow-md text-center">
                    <div className="flex items-center justify-center mb-2">
                      {icons[i % icons.length]}
                    </div>
                    <div className="font-bold">
                      <span className="text-3xl">
                        {language === 'en' ? stat.heading_eng : stat.heading_tamil}
                      </span>
                    </div>
                    <div className="text-teal-100 text-sm mt-1">
                      {language === 'en' ? stat.content_eng : stat.content_tamil}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

      </div>

    </div>
  );
};

export default Dccws;